// @core/stores/platform/PlatformFederationStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { platformApi } from '@core/api';
import type {
  AcceptFederationPeerRequest,
  CheckFederationHealthRequest,
  ConsentRecord,
  ConsentRecordResponse,
  ConsentStatusResponse,
  DiscoverPlatformsResponse,
  FederationHealthCheckResponse,
  FederationPeerResponse,
  GetFederationHealthHistoryResponse,
  GrantDataConsentRequest,
  PlatformRegistryEntryResponse,
  RegisterInRegistryRequest,
  ResolveFederatedResourceResponse,
  RevokeDataConsentRequest,
  RevokeFederationPeerRequest,
  SyncFederationCatalogRequest,
  SyncFederationCatalogResponse,
  UpdateFederationPeerRequest,
} from '@core/types/platform';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class PlatformFederationStore {
  // List state
  items: ConsentRecord[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: ConsentRecord | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, ConsentRecord>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): ConsentRecord | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: ConsentRecord | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Accept federation peering
   */
  async AcceptFederationPeer(peer_id: string, data: AcceptFederationPeerRequest): Promise<FederationPeerResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.AcceptFederationPeer(peer_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Check federation health
   */
  async CheckFederationHealth(peer_id: string, data: CheckFederationHealthRequest): Promise<FederationHealthCheckResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.CheckFederationHealth(peer_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Discover platforms in registry
   */
  async DiscoverPlatforms(params?: { }): Promise<DiscoverPlatformsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.DiscoverPlatforms(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get consent status
   */
  async GetConsentStatus(params?: { }): Promise<ConsentStatusResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GetConsentStatus(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get federation health history
   */
  async GetFederationHealthHistory(peer_id: string): Promise<GetFederationHealthHistoryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GetFederationHealthHistory(peer_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get federation peer
   */
  async GetFederationPeer(peer_id: string): Promise<FederationPeerResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GetFederationPeer(peer_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get registered platform
   */
  async GetRegisteredPlatform(registry_platform_id: string): Promise<PlatformRegistryEntryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GetRegisteredPlatform(registry_platform_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Grant data consent
   */
  async GrantDataConsent(data: GrantDataConsentRequest): Promise<ConsentRecordResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.GrantDataConsent(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List consent records
   */
  async ListConsentRecords(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await platformApi.ListConsentRecords(params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Register platform in registry
   */
  async RegisterInRegistry(data: RegisterInRegistryRequest): Promise<PlatformRegistryEntryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.RegisterInRegistry(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resolve federated resource
   */
  async ResolveFederatedResource(params?: { }): Promise<ResolveFederatedResourceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.ResolveFederatedResource(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revoke data consent
   */
  async RevokeDataConsent(consent_id: string, data: RevokeDataConsentRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.RevokeDataConsent(consent_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revoke federation peering
   */
  async RevokeFederationPeer(peer_id: string, data: RevokeFederationPeerRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.RevokeFederationPeer(peer_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Force federation catalog sync
   */
  async SyncFederationCatalog(peer_id: string, data: SyncFederationCatalogRequest): Promise<SyncFederationCatalogResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.SyncFederationCatalog(peer_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update federation peer
   */
  async UpdateFederationPeer(peer_id: string, data: UpdateFederationPeerRequest): Promise<FederationPeerResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await platformApi.UpdateFederationPeer(peer_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const platformFederationStore = new PlatformFederationStore();
